import isArray from 'lodash/isArray';
import isFinite from 'lodash/isFinite';
import isNumber from 'lodash/isNumber';
import map from 'lodash/map';
import range from 'lodash/range';

import add from 'date-fns/add';
import format from 'date-fns/format';
import formatDuration from 'date-fns/formatDuration';
import parseISO from 'date-fns/parseISO';

import { ASSAY_RUN_STATUS } from '../constants/assay.constants';
import COLORS from '../constants/colors.constants';
import { scientificNotationFormatter } from './formatters';
import { AssayConfig } from '../components/assays/AssayConfig';
import {
  SummarizerMetadata,
  RawSummarizerMetadata,
  RawRunSummary,
  RunSummary, RawAssayResults,
} from '../types/assay.types';

// @ts-ignore - Default empty obj assigment flagged by typescript
export const mapSummarizerMetadata = (summarizerMeta: RawSummarizerMetadata = {}): SummarizerMetadata => ({
  type: summarizerMeta.type,
  binMode: summarizerMeta.bin_mode,
  aggregation: summarizerMeta.aggregation,
  metric: summarizerMeta.metric,
  numBins: summarizerMeta.num_bins,
  binWeights: summarizerMeta.bin_weights,
  providedEdges: summarizerMeta.provided_edges,
})

/**
 * @deprecated Assays V1 Logic
 */
const formatBaselineDate = dateObj => format(dateObj, "yyyy-MM-dd'T'HH:mm:ssxxx")

/**
 * @deprecated Assays V1 Logic
 */
export const mapAssayDetails = (assayObj: any = {}) => {
  const summarizerRaw = assayObj.summarizer;
  const summarizerMetadata = summarizerRaw ? mapSummarizerMetadata(summarizerRaw) : {};

  const start = assayObj.baseline?.static?.start ? assayObj.baseline.static.start : null;
  const end = assayObj.baseline?.static?.end ? assayObj.baseline.static.end : null;
  const startDate = start ? formatBaselineDate(parseISO(start)) : null;
  const endDate = end ? formatBaselineDate(parseISO(end)) : null;

  return ({
    id: assayObj.id,
    name: assayObj.name,
    iopath: assayObj?.window.path,
    active: assayObj.active,
    status: assayObj.status,
    alertThreshold: assayObj.alert_threshold,
    pipelineID: assayObj.pipeline_id,
    pipelineName: assayObj.pipeline_name,
    lastRun: assayObj.last_run,
    lastWindowStart: assayObj.last_window_start,
    baselineStart: startDate,
    baselineEnd: endDate,
    modelName: assayObj.baseline?.calculated?.fixed_window?.model_name,
    runUntil: assayObj.run_until,
    created: assayObj.created,
    updatedAt: assayObj.updated_at,
    summarizerMetadata,
    window: assayObj.window
  })
}

// @ts-ignore - Default empty obj assigment flagged by typescript
export const mapAssayRunSummary = (runResult: RawRunSummary = {}): RunSummary => {
  const statistics = runResult?.statistics;
  const bins = runResult?.bins;
  return ({
    count: isNumber(runResult.count) ? runResult.count : statistics?.count,
    max: isNumber(runResult.max) ? runResult.max : statistics?.max,
    min: isNumber(runResult.min) ? runResult.min : statistics?.min,
    median: isNumber(runResult.median) ? runResult.median : statistics?.median,
    mean: isNumber(runResult.mean) ? runResult.mean : statistics?.mean,
    std: isNumber(runResult.std) ? runResult.std : statistics?.std,
    var: isNumber(runResult.std) ? runResult.std * runResult.std : statistics.std * statistics.std,
    edges: runResult.edges ? runResult.edges : bins.edges,
    edgeNames: runResult.edge_names ? runResult.edge_names : bins.labels,
    // @ts-ignore this fallback logic might not make sense - aggregated_values doesn't seem to exist on statistics (based on API definition)
    aggregatedValues: runResult.aggregated_values ? runResult.aggregated_values : statistics?.aggregated_values,
    aggregation: runResult.aggregation,
    start: runResult.start,
    end: runResult.end,
  });
}

/**
 * @deprecated Assays V1 Logic
 */
export const mapAssayResults = (assayResult: any, _assayConfig: any = {}) => {
  const summarizerRaw = assayResult.summarizer;
  const summarizerKeys = Object.keys(summarizerRaw);
  const summarizerType = summarizerKeys?.length ? summarizerKeys[0] : null;

  // @ts-ignore
  const summarizerMetadata = summarizerRaw ? mapSummarizerMetadata(summarizerRaw[summarizerType]) : {};

  // @ts-ignore
  summarizerMetadata.type = summarizerType;

  const baselineSummary = mapAssayRunSummary(assayResult.baseline_summary);
  const windowSummary = mapAssayRunSummary(assayResult.window_summary);

  return ({
    id: assayResult.id,
    assayId: assayResult.assay_id,
    index: assayResult?.bin_index,
    baselineSummary,
    windowSummary,
    created: assayResult.created_at,
    updatedAt: assayResult.updated_at,
    analyzedAt: assayResult.analyzed_at,
    windowStartTime: assayResult.last_window_start,
    elapsedTime: assayResult.elapsed_millis,
    // @ts-ignore
    iopath: windowSummary?.path || assayResult?.iopath,
    alertThreshold: assayResult.alert_threshold,
    score: assayResult.score,
    status: assayResult.status,
    scores: assayResult.scores,
    summarizerMetadata,
  });
}

// TODO - determine if updatedAt is supposed to come from analyzed_at
// @ts-ignore - Default empty obj assigment flagged by typescript
export const mapOnlyAssayResults = (assayResult: RawAssayResults = {}) => ({
  baselineSummary: mapAssayRunSummary(assayResult.baseline_summary),
  windowSummary: mapAssayRunSummary(assayResult.window_summary),
  created: assayResult.created_at,
  // @ts-ignore - updated_at does not exist - should this be analyzed_at?
  updatedAt: assayResult.updated_at,
  score: assayResult.score,
  status: assayResult.status,
  scores: assayResult.scores,
  summarizerMetadata: mapSummarizerMetadata(assayResult.summarizer),
});

// TODO - Convert configMap to a strictly defined type in AssayCreationContext
// Then remove ts-ignore statements in this function
export const newAssayConfiguration = ({
  pipelineID,
  pipelineName,
  workspaceID,
  configMap = {}
}): AssayConfig => ({
  // @ts-ignore
  name: configMap.assayName,
  pipeline_id: pipelineID,
  pipeline_name: pipelineName,
  workspace_id: workspaceID,
  // @ts-ignore
  alert_threshold: configMap.alertThreshold,
  // @ts-ignore
  lastRun: configMap.lastRun,
  baseline: {
    // @ts-ignore
    start: configMap.baselineStart,
    // @ts-ignore
    end: configMap.baselineEnd,
    // @ts-ignore
    width: configMap.baselineWidth,
    // @ts-ignore
    path: configMap?.iopath || configMap?.window?.path,
    pipeline_name: pipelineName,
    workspace_id: workspaceID,
  },
  // @ts-ignore
  window: {
    // @ts-ignore
    start: configMap.windowStart,
    // @ts-ignore
    interval: configMap.schedulingMode,
    // @ts-ignore
    path: configMap?.iopath || configMap?.window?.path,
    pipeline_name: pipelineName,
    workspace_id: workspaceID,
  },  // @ts-ignore

  summarizer: {
    // @ts-ignore
    type: configMap.aggType,
    // @ts-ignore
    aggregation: configMap.aggregation,
    // @ts-ignore
    metric: configMap.metric,
    // @ts-ignore
    bin_mode: configMap.binMode,
  },
})

/**
 * @deprecated Assays V1 Logic
 */
export const joinDetailsAndRunResults = (assayList: any[] = [], resultList: any[] = []) => {
  if (!assayList?.length) {
    return assayList;
  }

  return assayList.map((rawAssayEntry) => {
    const assayEntry = mapAssayDetails(rawAssayEntry);
    const assayID = assayEntry?.id;

    const assayResults = resultList?.length ? resultList.filter(assayRun => assayRun?.assay_id === assayID).map(mapAssayResults) : [];

    return {
      ...assayEntry,
      assayResults,
    }
  })
}

// TODO - Replace any types with strictly defined Types for Mapping Assay Run Results to Point Chart Datasets
// Then replace any type assignments
// score.score pattern introduced as part of assays v2
export const mapAssayPointData = (assayRunResults: any[] = []): any => assayRunResults?.map(runEntry => ({
  y: isNumber(runEntry?.score) ? runEntry.score : runEntry.score?.score,
  x: runEntry?.window_start ? parseISO(runEntry.window_start) : (runEntry?.windowSummary?.start ? parseISO(runEntry.windowSummary?.start) : parseISO(runEntry?.window_summary?.start)),
  status: runEntry?.error ? 'Alert' : runEntry?.status,
  color: runEntry?.status === ASSAY_RUN_STATUS.ALERT ? COLORS.error : COLORS.success,
  error: runEntry?.error || null,
}));

const mapBarDataForRunSummary = runSummary => ({
  data: [...(runSummary?.aggregatedValues ?? runSummary?.aggregated_values ?? [])],
  labels: [...(runSummary?.edgeNames ?? runSummary?.bins?.labels ?? [])],
  count: runSummary?.count,
})

export const deriveAssayChartDatasetsByStatus = (assayName, assayPoints = []) => [
  {
    id: `${assayName}-assay-points`,
    data: assayPoints,
  }
]

// Default to scientific notation
export const getRoundedRangeLabel = numInputA => scientificNotationFormatter.format(numInputA)

export const mapAssayBarLabels = (assayEntryWithResults) => {
  const edgeValues = [
    ...(assayEntryWithResults?.baselineSummary?.edges ?? assayEntryWithResults?.baseline_summary?.edges ?? assayEntryWithResults?.baseline_summary?.bins.edges ?? [])
  ];

  const edgeNames = [
    ...(assayEntryWithResults?.baselineSummary?.edgeNames ?? assayEntryWithResults?.baseline_summary?.edge_names ?? assayEntryWithResults?.baseline_summary?.bins.labels ?? [])
  ]

  const valuesToDisplay = [
    'Min',
    ...edgeValues.splice(0, edgeValues.length - 1),
    'Max',
  ];

  const rangeOfEdges = range(valuesToDisplay.length - 1)

  return map(rangeOfEdges, (index) => {
    const edgeValue = valuesToDisplay[index];
    const nextEdgeValue = valuesToDisplay[index + 1];

    if (!isNumber(edgeValue) && isNumber(nextEdgeValue)) {
      return [`${edgeValue} - ${scientificNotationFormatter.format(nextEdgeValue)}`, edgeNames[index]]
    }

    if (isNumber(edgeValue) && !isNumber(nextEdgeValue)) {
      return [`${scientificNotationFormatter.format(edgeValue)} - ${nextEdgeValue}`, edgeNames[index]]
    }

    if (isNumber(edgeValue) && isNumber(nextEdgeValue)) {
      return [getRoundedRangeLabel(edgeValue), edgeNames[index]];
    }

    // Unlikely default case
    return [`${edgeValue} - ${nextEdgeValue}`, edgeNames[index]];
  })
}

export const mapAssayBarData = (assayEntryWithResults) => {
  const windowChartData = mapBarDataForRunSummary(assayEntryWithResults?.windowSummary ?? assayEntryWithResults?.window_summary);
  const baselineChartData = mapBarDataForRunSummary(assayEntryWithResults?.baselineSummary ?? assayEntryWithResults?.baseline_summary);

  const labels = mapAssayBarLabels(assayEntryWithResults);

  return {
    window: windowChartData?.data?.length ? windowChartData.data.map((val, index) => ({
      y: val,
      labelVal: labels[index][0],
      label: labels[index][1],
      x: `${labels[index][0]}\n${labels[index][1]}`
    })) : [],
    baseline: baselineChartData?.data?.length ? baselineChartData.data.map((val, index) => ({
      y: val,
      labelVal: labels[index][0],
      label: labels[index][1],
      x: `${labels[index][0]}\n${labels[index][1]}`
    })) : [],
  }
}

export const deriveEmptyEdgesArrayFromNumberOfBins = (numberOfBins = 1) => range(numberOfBins + 1).map(() => null);
export const deriveEmptyWeightsArrayFromNumberOfBins = (numberOfBins = 1) => range(numberOfBins + 2).map(() => null);

export const padEndOfArrayWithNulls = (inputArr = [], numNullValues = 1) => {
  const nulls = range(numNullValues).map(() => null)

  return [
    ...inputArr,
    ...nulls
  ]
};

// Num Edges = Num Bins + 1
// Num Weights = Num Bins + 2 = Number of Table Rows
export const transformBinEdgesToTableData = (binEdges = [], binWeights = []) => {
  const numberOfWeights = binWeights.length;
  return binWeights.map((weightValue, index) => {
    const binLabel = index === 0 ? 'Left Outlier' : index === numberOfWeights - 1 ? 'Right Outlier' : `Bin ${index}`;
    const binWeight = binWeights?.length > 0 ? binWeights[index] : undefined;
    const fromValue = index === 0 ? 'Min' : binEdges[index - 1];
    const toValue = index === numberOfWeights - 1 ? 'Max' : binEdges[index];
    return ({
      binLabel,
      binWeight,
      fromValue,
      toValue,
    })
  });
}

export const areEdgesRequiredAndIncomplete = (assayConfig) => {
  const binMode = assayConfig.summarizer.bin_mode;
  const binEdges = assayConfig.summarizer.provided_edges;
  return binMode === 'Provided' && (!binEdges.length || binEdges.includes(null))
}

export const areWeightsRequiredAndIncomplete = (assayConfig) => {
  const binWeights = assayConfig.summarizer.bin_weights;

  return binWeights?.length && binWeights.includes(null);
}

// Checks to make sure that edge values are given in the expected order
export const areEdgeValuesInappropriate = (assayConfig) => {
  const binMode = assayConfig.summarizer.bin_mode;
  if (binMode !== 'Provided') {
    return false;
  }

  const binEdges = assayConfig.summarizer.provided_edges?.length ? [...assayConfig.summarizer.provided_edges] : [];
  const originalEdges = [...binEdges];
  const sortedEdges = binEdges.sort();

  return !sortedEdges.every((value, index) => value === originalEdges[index]);
}

/**
 * @deprecated Assays V1 Logic
 */
// @ts-ignore
export const deriveLabelsForBucketData = (buckets = []) => buckets.map(bucket => getRoundedRangeLabel(bucket[0], bucket[bucket.length - 1]))

// get all output 0 0. TODO: Break iopath into more chunks, maybe in MI?
export const getBaselineSamplesFromResponse = (baselineResponse, iopath, useArrowLogFormat) => map(baselineResponse, (log) => {
  const parts = iopath.split(' ')
  if (useArrowLogFormat) {
    const prefix = parts[0] === 'output' ? 'out' : 'in'
    const name = `${prefix}.${parts[1]}`
    const index = parseInt(parts[2], 10)
    const path = log[name];

    if (isFinite(path)) {
      return path;
    }

    if (isArray(path)) {
      return path[index];
    }

    // Intentional warning
    // eslint-disable-next-line no-console
    console.warn('Baselines of this shape are not supported.');
    return null;
  }
  const name = parts[1]
  const index = parseInt(parts[2], 10)
  return parts[0] === 'output' ? log.outputs[name].Float.data[index] : log.original_data.tensor[name][index]
})

/**
 * @deprecated Assays V1 Logic
 */
const calculateIQR = (arr) => {
  const sorted = arr.sort((a, b) => a - b);
  const len = arr.length;
  const n = (len % 2 === 0) ? len / 2 : (len / 2) + 1;
  const q1MedianFloor = Math.floor(n / 2);
  const q3MedianFloor = Math.floor(n * 1.5);
  const q1 = (n % 2 === 0) ? (sorted[q1MedianFloor] + sorted[q1MedianFloor + 1]) / 2 : sorted[q1MedianFloor + 1];
  const q3 = (n % 2 === 0) ? (sorted[q3MedianFloor] + sorted[q3MedianFloor + 1]) / 2 : sorted[q3MedianFloor + 1];
  return q3 - q1;
}

/**
 * @deprecated Assays V1 Logic
 */
export const freedmanDiaconisBinDetection = (arr) => {
  const IQR = calculateIQR(arr);
  const denom = Math.cbrt(arr.length);
  return 2 * IQR / denom;
}

// Of the form `{num} {duration}`
/**
 * @deprecated Assays V1 Logic
 */
export const parseWindowWidth = (windowWidth, isAssaysV2Enabled) => {
  // V2 Logic
  if (isAssaysV2Enabled) {
    // TODO: Add v2 logic here
    const seconds = parseInt(windowWidth, 10)
    return formatDuration({ seconds })
  }

  const [numeric, durationStr] = windowWidth.split(' ');

  // Is this ever not an int?
  const num = parseFloat(numeric);
  switch (durationStr) {
  case 'milliseconds':
  case 'millisecond':
    return { 'milliseconds': num };
  case 'seconds':
  case 'second':
    return { 'seconds': num };
  case 'minutes':
  case 'minute':
    return { 'minutes': num };
  case 'hours':
  case 'hour':
    return { 'hours': num };
  case 'days':
  case 'day':
    return { 'days': num };
  case 'weeks':
  case 'week':
    return { 'weeks': num };
  case 'months':
  case 'month':
    return { 'months': num };
  default:
    return { 'weeks': num };
  }
}

// Previously calculateLastWindowEnd
/**
 * @deprecated Assays V1 Logic
 */
const parseLastRun = (assayConfig, isAssaysV2Enabled) => {
  // V2 Logic- last run calculated by result
  if (isAssaysV2Enabled) {
    // TODO: Add v2 logic here
    return ''
  }

  // If a Last Run value has been provided by the API
  if (assayConfig.lastRun) {
    return parseISO(assayConfig.lastRun);
  }

  // Legacy Logic - determine if this is still needed
  // If a Last Run value has not been provided by the API
  if (assayConfig.lastWindowStart && assayConfig.window?.width) {
    const windowWidthObj = parseWindowWidth(assayConfig.window.width, isAssaysV2Enabled);
    const lastWindowStartDate = parseISO(assayConfig.lastWindowStart);

    // @ts-ignore
    return add(lastWindowStartDate, windowWidthObj);
  }

  // This assay hasn't been run yet, but this is the intended start time.
  if (assayConfig.window?.start) {
    return parseISO(assayConfig.window.start);
  }

  // There's no start time, so use the baseline's end time.
  return parseISO(assayConfig.baselineEnd);
};

// NextRun is no longer a field supported by MI. It is now calculated by the following alg:
// last_window_end + window.width = next_run
// where
// last_window_end = last_window_start + window.width
/**
 * @deprecated Assays V1 Logic
 */
export const calculateNextRun = (assayConfig, assayDetailLastRun, isAssaysV2Enabled) => {
  // The above comment is technically wrong. The next run is `last_window_end + interval`.
  const windowWidthObj = parseWindowWidth(assayConfig.window.interval, isAssaysV2Enabled);
  const lastWindowEnd = parseLastRun(assayConfig, isAssaysV2Enabled);

  // @ts-ignore
  return add(lastWindowEnd, windowWidthObj);
}
